import { FileText } from "lucide-react";
import { Req } from "../../components/ui/kit";
import { COMMON_UNITS } from "../../lib/fieldOptions";
import { PROJECT_DOCUMENT_ACCEPT } from "../../lib/projectDocuments";
import { fmtSize } from "./model";
import type { PostFlowPublicationModel } from "./createPostFlowPublication";
import { ProjectRequirementsEditor } from "./ProjectRequirementsEditor";

export function createProjectRfqPostingFields(model: PostFlowPublicationModel) {
  const {
    type, current, translate, map, examples, projectRequirements, setProjectRequirements,
    projectLicenses, setProjectLicenses, selectedProjectLicenses, setSelectedProjectLicenses,
    projectDocuments, setProjectDocuments, images, rfqItems, setItem, addItem, removeItem,
    addProjectDocuments
  } = model;
  const projectQualificationsField = <ProjectRequirementsEditor
                  requirements={projectRequirements}
                  licenses={projectLicenses}
                  selectedLicenses={selectedProjectLicenses}
                  onSelectedLicensesChange={setSelectedProjectLicenses}
                  onChange={(kind, entries) => {
                    if (kind === "requirements") setProjectRequirements(entries);
                    if (kind === "licenses") setProjectLicenses(entries);
                  }}
                />;
  const rfqLineItemsField = <div>
                  <label className="label">Line items <Req /></label>
                  <p className="text-xs text-mut mb-2">Choose <b>By quantity</b> for materials priced per unit, or <b>Lump sum</b> for properties, whole businesses or one-off scopes that shouldn&apos;t be forced into a quantity.</p>
                  <div className="space-y-2">
                    {rfqItems.map((row, i) => (
                      <div key={i} className="rounded-lg border border-line p-2.5 space-y-2">
                        <div className="flex gap-2">
                          <input className="field flex-1" value={row.name} onChange={(e) => setItem(i, "name", e.target.value)} placeholder={row.kind === "lump" ? examples.rfqLineItems?.lumpName : examples.rfqLineItems?.quantityName} />
                          <select className="field w-32 shrink-0" value={row.kind} onChange={(e) => setItem(i, "kind", e.target.value)}>
                            <option value="quantity">By quantity</option>
                            <option value="lump">Lump sum</option>
                          </select>
                          <button type="button" onClick={() => removeItem(i)} disabled={rfqItems.length === 1} className="btn-outline h-10 px-2 text-xs text-brand-red disabled:opacity-40 shrink-0">✕</button>
                        </div>
                        <div className="flex gap-2">
                          {row.kind === "quantity" && (<>
                            <input className="field w-24" inputMode="numeric" value={row.qty} onChange={(e) => setItem(i, "qty", e.target.value)} placeholder={examples.rfqLineItems?.quantity} />
                            <input className="field w-28" list="rfq-unit-options" value={row.unit} onChange={(e) => setItem(i, "unit", e.target.value)} placeholder={examples.rfqLineItems?.unit} />
                          </>)}
                          <input className="field flex-1" value={row.spec} onChange={(e) => setItem(i, "spec", e.target.value)} placeholder={examples.rfqLineItems?.specifications} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <button type="button" onClick={addItem} className="btn-outline mt-2 h-9 text-xs">+ Add item</button>
                  <datalist id="rfq-unit-options">{COMMON_UNITS.map((u) => <option key={u} value={u} />)}</datalist>
                </div>;
  const projectDocumentsField = <section className="min-w-0 rounded-xl border border-line p-4">
                    <h4 className="text-sm font-semibold text-navy">Project documents <span className="font-normal text-mut">({translate("posting.optionalLabel")})</span></h4>
                    <p className="mt-1 text-xs text-mut">Share files that help providers understand the work and prepare an accurate bid. For example:</p>
                    <ul className="mt-2 list-disc space-y-1 pl-4 text-xs text-mut">
                      <li>Project brief or requirements — tasks, deliverables, and expected quality.</li>
                      <li>Material list or bill of quantities — specifications, quantities, and who supplies them.</li>
                      <li>Plans, drawings, or reference images — dimensions, layout, or the desired result.</li>
                      <li>Schedule or site instructions — target dates, access hours, and work restrictions.</li>
                    </ul>
                    <p className="mt-3 text-xs text-mut">Export documents as PDF or attach JPG, PNG, and WebP images; up to 25 MB per file.</p>
                    <input id="project-documents" type="file" multiple accept={PROJECT_DOCUMENT_ACCEPT} className="hidden" onChange={(event) => { addProjectDocuments(event.target.files); event.currentTarget.value = ""; }} />
                    <label htmlFor="project-documents" className="btn-outline mt-3 inline-flex h-9 cursor-pointer px-3 text-xs"><FileText className="h-4 w-4" />Add files</label>
                    {projectDocuments.length > 0 && <ul className="mt-3 divide-y divide-line rounded-lg border border-line px-3">
                      {projectDocuments.map((file, index) => <li key={file.name + file.size} className="flex items-center justify-between gap-3 py-2 text-xs">
                        <span className="min-w-0 truncate text-navy">{file.name} <span className="text-mut">· {fmtSize(file.size)}</span></span>
                        <button type="button" className="shrink-0 text-brand-red" onClick={() => setProjectDocuments((current) => current.filter((_, fileIndex) => fileIndex !== index))}>Remove</button>
                      </li>)}
                    </ul>}
                  </section>;
  return {
    projectQualificationsField, rfqLineItemsField, projectDocumentsField
  };
}
